import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, X } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useApi, errMsg } from "@/lib/api";
import { currentSubscription, enablePush, disablePush, needsHomeScreen, pushSupported, syncPush } from "@/lib/push";

const friendly = (e) => (e?.message && !/registration failed|permission denied|AbortError/i.test(e.message) ? e.message : "Couldn't turn on notifications in this browser. Check that notifications are allowed for this site.");
const dismissKey = (id) => `jetset-push-prompt-dismissed:${id}`;
const synced = new Set();

export function PushPrompt({ me }) {
  const api = useApi();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!synced.has(me.id)) { synced.add(me.id); syncPush(api).catch(() => {}); }
    const asked = localStorage.getItem(dismissKey(me.id));
    if (asked) return;
    if (needsHomeScreen()) { setShow(true); return; }
    if (pushSupported() && Notification.permission === "default") setShow(true);
  }, [api, me.id]);
  if (!show) return null;
  const close = () => { localStorage.setItem(dismissKey(me.id), "1"); setShow(false); };
  const turnOn = async () => {
    setBusy(true);
    try { await enablePush(api); toast.success("Push notifications are on for this device"); close(); }
    catch (e) { toast.error(friendly(e)); } finally { setBusy(false); }
  };
  const ios = needsHomeScreen();
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4" data-testid="push-prompt">
      <div className="lux-card px-4 py-3 flex flex-wrap items-center gap-3">
        <BellRing size={16} className="text-[#C6A55C] shrink-0" />
        <p className="text-sm text-zinc-200 flex-1 min-w-[200px]">
          {ios ? "Get instant trip alerts on this iPhone: tap Share → Add to Home Screen, then open the portal from your Home Screen and turn alerts on in Profile."
            : "Get instant trip alerts on this device, even when the portal is closed."}
        </p>
        {!ios && <button type="button" className="frame-btn gold !py-2 !px-4" disabled={busy} onClick={turnOn} data-testid="push-prompt-enable-btn">{busy ? "Turning on…" : "Turn on notifications"}</button>}
        <button type="button" className="p-1.5 text-zinc-500 hover:text-white transition-colors" onClick={close} aria-label="Not now" data-testid="push-prompt-dismiss-btn"><X size={16} /></button>
      </div>
    </div>
  );
}

export function PushSetting() {
  const api = useApi();
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { currentSubscription().then((s) => setOn(!!s && Notification.permission === "granted")).catch(() => {}); }, []);
  const change = async (next) => {
    setBusy(true);
    try {
      if (next) { await enablePush(api); toast.success("Push notifications are on for this device"); } else { await disablePush(api); toast.success("Push notifications are off for this device"); }
      setOn(next);
    } catch (e) { toast.error(friendly(e)); } finally { setBusy(false); }
  };
  const test = async () => {
    try { const { data } = await api.post("/push/test"); toast[data.sent ? "success" : "error"](data.sent ? "Test sent. It should pop up in a few seconds." : data.devices ? "Couldn't reach this device. Turn notifications off and on again." : "No device is set up for push yet."); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="lux-card p-6 self-start" data-testid="push-setting">
      <p className="eyebrow mb-3">Push notifications</p>
      <div className="flex items-center gap-3">
        <Switch checked={on} disabled={busy} onCheckedChange={change} aria-label="Push notifications" data-testid="push-toggle" />
        <span className="text-sm text-zinc-200">{on ? "On for this device" : "Off for this device"}</span>
        {on && <button type="button" className="ml-auto text-[0.62rem] tracking-[0.2em] uppercase text-zinc-400 hover:text-[#5B7FD1]" onClick={test} data-testid="push-test-btn">Send test</button>}
      </div>
      <p className="text-[11px] text-zinc-500 mt-3">Every alert in your bell also pops up on this device. Turn it on separately on each computer or phone you use.{needsHomeScreen() ? " On iPhone or iPad, add the portal to your Home Screen first and open it from there." : ""}</p>
    </div>
  );
}
